import { color } from "@styles";
import { BetterButton } from "components/atoms/BetterButton";
import { DesktopOnly, NotMobile, mobile, tablet } from "components/templates/Breakpoints";
import { FC, useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { HubspotForms, isEmail } from "@utils";
import { useHubspotForm } from "hooks/useHubspotForm";
import Bugsnag from "@bugsnag/js";
import { ConfirmationModal } from "components/resources/resourceHub/ConfirmationModal";
import {
  GDPR_LOCAL_STORAGE,
  SIGNUP_DISMISSED_LOCAL_STORAGE,
  SIGNUP_SUBMITTED_LOCAL_STORAGE,
} from "utils/_constants";
import { AlternateFormField } from "components/atoms/AlternateFormField";

type Location = "advisor" | "dynamic";

interface Props {
  location: Location;
}

const FloatingSubscriptionPopup: FC<Props> = ({ location }) => {
  const [showFormModal, setShowFormModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const [email, setEmail] = useState("");
  const [fakeEmail, setFakeEmail] = useState("");

  const [error, setError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const { handleFormSubmit } = useHubspotForm();

  const handleShakeComplete = () => {
    setEmailError(false);
  };

  const openConfirmationModal = () => {
    setShowConfirmationModal(true);
  };

  const closeConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const handleDismiss = () => {
    setShowFormModal(false);
    if (location === "dynamic") {
      window.localStorage.setItem(SIGNUP_DISMISSED_LOCAL_STORAGE, "true");
    }
  };

  const handleSubmit = async () => {
    setError(false);

    if (fakeEmail) {
      return;
    }

    if (!isEmail(email)) {
      setEmailError(true);
      return;
    }

    const formData = {
      fields: [{ name: "email", value: email }],
      context: {
        pageUri: window ? window.location.href : "https://yulife.com/",
        pageName: window ? document.title : "",
      },
    };

    try {
      await handleFormSubmit(HubspotForms.blogSignUp, formData);
    } catch (e) {
      Bugsnag.notify(e);
      setError(true);
    }

    window.localStorage.setItem(SIGNUP_SUBMITTED_LOCAL_STORAGE(location), "true");
    openConfirmationModal();
    setShowFormModal(false);
  };

  useEffect(() => {
    const advisorSubmitted = window.localStorage.getItem(SIGNUP_SUBMITTED_LOCAL_STORAGE("advisor"));
    const dynamicSubmitted = window.localStorage.getItem(SIGNUP_SUBMITTED_LOCAL_STORAGE("dynamic"));
    const dismissed = window.localStorage.getItem(SIGNUP_DISMISSED_LOCAL_STORAGE);
    const gdpr = window.localStorage.getItem(GDPR_LOCAL_STORAGE);

    // In the event that the user hasn't accepted our gdpr terms, don't show signup modal. Two modals on-screen is mad

    if (advisorSubmitted || dynamicSubmitted || !gdpr || (location === "dynamic" && dismissed)) {
      return;
    }

    const handleScroll = () => {
      if (window.scrollY > 3000) {
        setShowFormModal(true);
        setTimeout(() => {
          setFadeIn(true);
        });

        window.removeEventListener("scroll", handleScroll);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [location]);

  if (showFormModal) {
    return (
      <>
        <Spacing $fadeIn={fadeIn}>
          <Panel $isShaking={emailError} onAnimationEnd={handleShakeComplete}>
            <CloseButton
              src="/static/icons/Close.svg"
              width={24}
              height={24}
              alt="Close icon"
              onClick={handleDismiss}
            />
            <FormWrap>
              <Title>Would you like more from YuLife?</Title>
              <Subtitle>
                You can subscribe to us for news, content and events.
                <NotMobile>
                  By submitting your information, you agree to YuLife's Privacy Policy
                </NotMobile>
              </Subtitle>
              <InputContainer>
                <Input
                  type="email"
                  placeholder="Enter your work email"
                  onChange={(e) => setEmail(e.target.value)}
                  $emailError={emailError}
                />
                <AlternateFormField onChange={(e) => setFakeEmail(e.target.value)} />
                <SubmitButton width="35%" text="Sign up here" onClick={handleSubmit} />
              </InputContainer>
            </FormWrap>
          </Panel>
        </Spacing>
      </>
    );
  }

  if (showConfirmationModal) {
    return (
      <ConfirmationModal
        closeOnClickOutside={true}
        showModal={showConfirmationModal}
        closeModal={closeConfirmationModal}
        message={{
          header: error
            ? "We couldn't add you to our mailing list this time. Please try again later."
            : "You’ve successfully signed up to our mailing list.",
          body: error
            ? "Still not working? Reach out to our support team for help."
            : "Sit back, relax and enjoy the read! ",
        }}
      />
    );
  }
};

export default FloatingSubscriptionPopup;

const Spacing = styled.div<{ $fadeIn: boolean }>`
  opacity: ${(p) => (p.$fadeIn ? "1" : "0")};
  transition: opacity 0.3s ease;
  position: fixed;
  z-index: 3;
  bottom: 0;
  right: 0;
  margin: 0 1rem 1rem 0;

  ${mobile} {
    margin: 1rem;
  }
`;

const shakeAnimation = keyframes`
  10%,
  90% {
    transform: translate3d(-1px, 0, 0);
  }

  20%,
  80% {
    transform: translate3d(2px, 0, 0);
  }

  30%,
  50%,
  70% {
    transform: translate3d(-4px, 0, 0);
  }

  40%,
  60% {
    transform: translate3d(4px, 0, 0);
  }
`;

const Panel = styled.div<{ $isShaking: boolean }>`
  display: inline-block;
  background: ${color.B210};
  margin: 0 auto;
  border-radius: 0.5rem;
  border: 1px solid ${color.B230};
  padding: 2rem;
  box-shadow: 0.5rem 0.5rem 0 rgba(92, 87, 87, 0.08);
  ${(p) =>
    p.$isShaking &&
    css`
      animation: ${shakeAnimation} 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97);
    `};
  transition: all 0.3s ease;

  ${mobile} {
    flex-direction: column;
    padding: 2rem;
    width: 100%;
  }
`;

const Title = styled.h4`
  margin: 0;
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  letter-spacing: 0.075rem;
  margin-bottom: 1.5rem;
`;

const InputContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  width: 100%;

  ${mobile} {
    flex-direction: column;
  }
`;

export const Input = styled.input<{ $emailError: boolean }>`
  border: 0.125rem solid #e3e3e1;
  border-radius: 0.5rem;
  padding: 1rem 1.5rem 1rem;
  font-size: 1.2rem;
  line-height: 2rem;
  letter-spacing: 0.075rem;
  color: ${color.B1100};
  width: 65%;

  &::placeholder {
    font-size: 1.25rem;
    color: ${color.B2100};
  }

  ${mobile} {
    width: 100%;
  }
`;

const SubmitButton = styled(BetterButton)`
  ${mobile} {
    width: 100%;
  }
`;

const FormWrap = styled.div`
  width: 37.5rem;

  ${mobile} {
    width: auto;
  }
`;

const CloseButton = styled.img`
  position: absolute;
  right: 0;
  top: 0;
  margin: 1rem 1rem 0 0;
  cursor: pointer;
`;
